<script setup>
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
  emptyStateMessage: {
    type: String,
    default: '',
  },
  navigable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['delete', 'navigate']);

const { t } = useI18n();

const handleDelete = itemId => {
  emit('delete', itemId);
};

const handleNavigate = item => {
  emit('navigate', item);
};
</script>

<template>
  <div
    v-if="isFetching"
    class="flex items-center justify-center py-6 w-full text-n-slate-11"
  >
    <Spinner />
  </div>
  <div
    v-else-if="items.length === 0 && emptyStateMessage"
    class="flex items-center justify-center gap-2 rounded-lg border border-dashed border-n-weak px-4 py-6 w-full text-center"
  >
    <Icon
      icon="i-hugeicons-information-circle"
      class="size-4 shrink-0 text-n-slate-10"
    />
    <span class="text-sm text-n-slate-11">
      {{ emptyStateMessage }}
    </span>
  </div>
  <ul
    v-else
    class="mb-0 flex flex-col divide-y divide-n-weak rounded-lg border border-n-weak"
  >
    <li
      v-for="item in items"
      :key="item.id"
      class="flex min-w-0 items-center gap-3 px-3 py-2"
    >
      <component
        :is="navigable ? 'button' : 'div'"
        :type="navigable ? 'button' : undefined"
        :aria-label="
          navigable
            ? t('ASSIGNMENT_POLICY.REDESIGN.COMMON.OPEN_INBOX', {
                name: item.name,
              })
            : undefined
        "
        class="group flex min-w-0 flex-1 items-center gap-2.5 rounded-md py-1 text-left"
        :class="
          navigable
            ? 'cursor-pointer hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand px-1.5 -mx-1.5'
            : ''
        "
        @click="navigable && handleNavigate(item)"
      >
        <span
          v-if="item.icon"
          class="grid size-7 shrink-0 place-items-center rounded-md bg-n-alpha-2"
        >
          <Icon :icon="item.icon" class="size-4 text-n-slate-12" />
        </span>
        <Avatar
          v-else
          :title="item.name"
          :src="item.avatarUrl"
          :name="item.name"
          :size="28"
          rounded-full
        />
        <span class="flex min-w-0 flex-col">
          <span
            :title="item.name"
            class="truncate text-sm font-medium text-n-slate-12"
            :class="navigable ? 'group-hover:text-n-brand' : ''"
          >
            {{ item.name }}
          </span>
          <span
            v-if="item.email || item.phoneNumber"
            :title="item.email || item.phoneNumber"
            class="truncate text-xs text-n-slate-11"
          >
            {{ item.email || item.phoneNumber }}
          </span>
        </span>
        <Icon
          v-if="navigable"
          icon="i-hugeicons-link-square-02"
          class="size-3.5 shrink-0 text-n-slate-10 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none"
        />
      </component>

      <Button
        v-tooltip.top="t('ASSIGNMENT_POLICY.REDESIGN.COMMON.REMOVE')"
        icon="i-hugeicons-delete-02"
        slate
        ghost
        sm
        type="button"
        class="shrink-0 hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
        :aria-label="
          t('ASSIGNMENT_POLICY.REDESIGN.COMMON.REMOVE_ARIA', {
            name: item.name,
          })
        "
        @click="handleDelete(item.id)"
      />
    </li>
  </ul>
</template>
